/**
 * PIEZAS CRO DEL CAJON DE CARRITO
 * ----------------------------------------------------------------------------
 * Dos componentes chicos: el reloj de reserva y el "Combinalo con".
 *
 * El markup NO se dibuja aca. Horizon rehace toda la seccion del header con
 * morphSection() en cada cambio del carrito (assets/component-cart-items.js),
 * asi que cualquier nodo creado desde el JS se borra en el primer +/- de
 * cantidad. Todo lo visible vive en Liquid; aca solo hay comportamiento.
 *
 * Tampoco hay polling: el original consultaba /cart.js cada 400 ms con el cajon
 * abierto. La barra de envio sale de cart.total_price en Liquid y vuelve sola
 * con el morph.
 */

import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { fetchConfig, prefersReducedMotion } from '@theme/utilities';

/* ===========================================================================
   1. RELOJ DE RESERVA
   =========================================================================== */

const CLAVE_RESERVA = 'fp_carrito_reserva';

/**
 * Cuenta regresiva "Tu carrito esta reservado por 9:47 min".
 *
 * El final se guarda en localStorage para que el reloj siga donde iba al
 * cambiar de pagina, y se renueva solo al llegar a cero (nunca queda en 0:00).
 *
 * @typedef {object} RefsReserva
 * @property {HTMLElement} [reloj]
 *
 * @extends {Component<RefsReserva>}
 */
class CartReserva extends Component {
  /** @type {number | undefined} */
  #intervalo;

  /** @type {AbortController | undefined} */
  #corte;

  /** Final de la reserva en ms. Respaldo en memoria si localStorage falla. */
  #fin = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#corte = new AbortController();
    const { signal } = this.#corte;

    // dialog:open no burbujea y se dispara sobre el propio cajon, asi que hay
    // que escucharlo ahi y no en document.
    const cajon = this.closest('cart-drawer-component');
    cajon?.addEventListener('dialog:open', this.#arrancar, { signal });

    // Con la pestaña tapada el intervalo no tiene a quien mostrarle la hora.
    document.addEventListener('visibilitychange', this.#segunPestana, { signal });

    // El morph repone el texto que mando el servidor ("10:00"): se repinta en
    // cuanto termina el redibujo para que no se vea el salto.
    document.addEventListener(ThemeEvents.cartUpdate, this.#trasRedibujo, { signal });

    this.#arrancar();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#parar();
    this.#corte?.abort();
  }

  /** Minutos de reserva, con piso de 1 para no dividir por cero. */
  get #minutos() {
    return Math.max(1, Number(this.dataset.minutos) || 10);
  }

  /** Solo corre con el cajon abierto y la pestaña a la vista. */
  #activo() {
    if (document.visibilityState === 'hidden') return false;

    const dialogo = this.closest('dialog');
    return dialogo ? dialogo.open : true;
  }

  #arrancar = () => {
    if (this.#intervalo || !this.#activo()) return;

    this.#fin = this.#leerFin();
    this.#pintar();
    this.#intervalo = setInterval(this.#latir, 1000);
  };

  #parar = () => {
    clearInterval(this.#intervalo);
    this.#intervalo = undefined;
  };

  #segunPestana = () => {
    if (document.visibilityState === 'hidden') this.#parar();
    else this.#arrancar();
  };

  #trasRedibujo = () => {
    requestAnimationFrame(() => this.#pintar());
  };

  #latir = () => {
    // El cajon se puede cerrar con Escape o clic afuera, que no avisan por
    // dialog:close en todos los casos: se comprueba y el reloj se apaga solo.
    if (!this.#activo()) return this.#parar();
    this.#pintar();
  };

  #pintar() {
    const { reloj } = this.refs;
    if (!reloj) return;

    if (!this.#fin) this.#fin = this.#leerFin();

    let resta = this.#fin - Date.now();
    if (resta <= 0) {
      this.#fin = this.#guardarFin(Date.now() + this.#minutos * 60000);
      resta = this.#fin - Date.now();
    }

    const m = Math.floor(resta / 60000);
    const s = Math.floor((resta % 60000) / 1000);
    const texto = `${m}:${s < 10 ? '0' : ''}${s}`;

    if (reloj.textContent !== texto) reloj.textContent = texto;
  }

  /** @returns {number} */
  #leerFin() {
    const ahora = Date.now();
    let fin = this.#fin;

    try {
      fin = Number(localStorage.getItem(CLAVE_RESERVA)) || fin;
    } catch {
      // Safari en privado tira al leer: se sigue con el valor en memoria.
    }

    if (!fin || fin <= ahora) return this.#guardarFin(ahora + this.#minutos * 60000);

    return fin;
  }

  /**
   * @param {number} fin
   * @returns {number}
   */
  #guardarFin(fin) {
    this.#fin = fin;

    try {
      localStorage.setItem(CLAVE_RESERVA, String(fin));
    } catch {
      // Sin storage el reloj vive solo lo que dure la pagina. Alcanza.
    }

    return fin;
  }
}

if (!customElements.get('fp-cart-reserva')) {
  customElements.define('fp-cart-reserva', CartReserva);
}

/* ===========================================================================
   2. COMBINALO CON
   =========================================================================== */

/**
 * Interruptores abiertos: id de producto -> id de variante elegida.
 * Va afuera de la clase porque el elemento puede ser reemplazado entero por el
 * morph y el usuario espera encontrar su eleccion donde la dejo.
 * @type {Map<string, string>}
 */
const abiertos = new Map();

/**
 * Carrusel de a 2 con interruptores.
 *
 * @typedef {object} RefsUpsell
 * @property {HTMLElement} [pista]
 * @property {HTMLElement[]} [puntos]
 *
 * @extends {Component<RefsUpsell>}
 */
class CartUpsell extends Component {
  /** @type {AbortController | undefined} */
  #corte;

  /** @type {number | undefined} */
  #esperaScroll;

  /** @type {number | undefined} */
  #esperaAviso;

  #pagina = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#corte = new AbortController();
    const { signal } = this.#corte;

    // scroll no burbujea: en fase de captura se escucha igual desde el padre,
    // y asi el listener sobrevive a cualquier redibujo de la pista.
    this.addEventListener('scroll', this.#alScroll, { capture: true, passive: true, signal });

    // El morph devuelve la lista tal cual la mando el servidor: todo cerrado y
    // en la primera pagina. Se repone lo que el usuario habia elegido.
    document.addEventListener(ThemeEvents.cartUpdate, this.#trasRedibujo, { signal });

    this.#restaurar();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#esperaScroll);
    clearTimeout(this.#esperaAviso);
    this.#corte?.abort();
  }

  /**
   * Enciende o apaga un interruptor.
   * Con una sola variante agrega derecho; con varias abre el selector de talle.
   * @param {Event} evento
   */
  alternar(evento) {
    const boton = /** @type {HTMLElement} */ (evento.target);
    const it = boton.closest('.fp-up__it');
    if (!(it instanceof HTMLElement)) return;

    const pid = it.dataset.pid ?? '';

    if (it.classList.contains('is-on')) {
      this.#encender(it, false);
      abiertos.delete(pid);
      return;
    }

    const selector = it.querySelector('.fp-up__sel');

    this.#encender(it, true);

    if (selector instanceof HTMLSelectElement) {
      abiertos.set(pid, selector.value);
      this.#refrescarPrecio(it, selector);
      selector.focus();
      return;
    }

    this.#enviar(it, it.dataset.vid, boton);
  }

  /**
   * Agrega la variante elegida en el selector.
   * @param {Event} evento
   */
  agregar(evento) {
    const boton = /** @type {HTMLElement} */ (evento.target);
    const it = boton.closest('.fp-up__it');
    if (!(it instanceof HTMLElement)) return;

    const selector = it.querySelector('.fp-up__sel');
    const id = selector instanceof HTMLSelectElement ? selector.value : it.dataset.vid;

    this.#enviar(it, id, boton);
  }

  /**
   * @param {Event} evento
   */
  elegirVariante(evento) {
    const selector = evento.target;
    if (!(selector instanceof HTMLSelectElement)) return;

    const it = selector.closest('.fp-up__it');
    if (!(it instanceof HTMLElement)) return;

    abiertos.set(it.dataset.pid ?? '', selector.value);
    this.#refrescarPrecio(it, selector);
  }

  /**
   * @param {number} indice
   */
  irPagina(indice) {
    const { pista } = this.refs;
    if (!pista) return;

    this.#pagina = indice;
    pista.scrollTo({
      left: indice * pista.clientWidth,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
    this.#marcarPunto(indice);
  }

  /**
   * @param {HTMLElement} it
   * @param {boolean} on
   */
  #encender(it, on) {
    it.classList.toggle('is-on', on);

    const sw = it.querySelector('.fp-up__sw');
    sw?.setAttribute('aria-checked', on ? 'true' : 'false');

    // inert y no hidden: cerrado no se puede tabular y el alto se puede animar.
    const exp = it.querySelector('.fp-up__exp');
    if (exp) exp.toggleAttribute('inert', !on);
  }

  /**
   * @param {HTMLElement} it
   * @param {HTMLSelectElement} selector
   */
  #refrescarPrecio(it, selector) {
    const opcion = selector.selectedOptions[0];
    const hoy = it.querySelector('.fp-up__hoy');

    if (opcion?.dataset.precio && hoy) hoy.textContent = opcion.dataset.precio;
  }

  /**
   * @param {number} indice
   */
  #marcarPunto(indice) {
    for (const [i, punto] of (this.refs.puntos ?? []).entries()) {
      punto.classList.toggle('is-on', i === indice);
    }
  }

  #alScroll = (/** @type {Event} */ evento) => {
    const pista = evento.target;
    if (!(pista instanceof HTMLElement) || !pista.classList.contains('fp-up__pista')) return;

    clearTimeout(this.#esperaScroll);
    this.#esperaScroll = setTimeout(() => {
      const indice = Math.round(pista.scrollLeft / Math.max(pista.clientWidth, 1));
      this.#pagina = indice;
      this.#marcarPunto(indice);
    }, 120);
  };

  #trasRedibujo = () => {
    // El morph es sincronico dentro del manejador del evento: un cuadro despues
    // la lista nueva ya esta en su lugar.
    requestAnimationFrame(() => this.#restaurar());
  };

  #restaurar() {
    for (const [pid, vid] of abiertos) {
      const it = this.querySelector(`.fp-up__it[data-pid="${pid}"]`);

      // Ya esta en el carrito o se agoto: se olvida.
      if (!(it instanceof HTMLElement)) {
        abiertos.delete(pid);
        continue;
      }

      const selector = it.querySelector('.fp-up__sel');
      if (selector instanceof HTMLSelectElement) {
        selector.value = vid;
        // Esa variante ya no existe.
        if (!selector.value) {
          abiertos.delete(pid);
          continue;
        }
        this.#refrescarPrecio(it, selector);
      }

      this.#encender(it, true);
    }

    const { pista } = this.refs;
    if (!pista || !this.#pagina) return;

    const paginas = Number(this.dataset.paginas) || 1;
    const indice = Math.min(this.#pagina, paginas - 1);

    if (indice > 0 && pista.clientWidth) {
      pista.scrollLeft = indice * pista.clientWidth;
      this.#marcarPunto(indice);
    }
  }

  /**
   * Agrega al carrito y avisa al tema para que redibuje.
   *
   * Se manda la lista de secciones igual que product-form.js: con el HTML
   * fresco en la respuesta, cart-items-component hace un morph y se evita un
   * segundo viaje al servidor.
   *
   * @param {HTMLElement} it
   * @param {string | undefined} id
   * @param {HTMLElement} boton
   */
  async #enviar(it, id, boton) {
    if (!id) return;

    const esBotonAgregar = boton.classList.contains('fp-up__add');
    const etiqueta = boton.textContent;

    boton.toggleAttribute('disabled', true);
    if (esBotonAgregar) boton.textContent = '…';

    const cuerpo = new FormData();
    cuerpo.append('id', id);
    cuerpo.append('quantity', '1');

    const secciones = Array.from(document.querySelectorAll('cart-items-component'))
      .map((nodo) => (nodo instanceof HTMLElement ? nodo.dataset.sectionId : null))
      .filter(Boolean);

    if (secciones.length) cuerpo.append('sections', secciones.join(','));

    const config = fetchConfig('javascript', { body: cuerpo });

    try {
      const respuesta = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const datos = await respuesta.json();

      // Shopify contesta 200 con status cuando no hay stock suficiente.
      if (datos.status) throw new Error(datos.description || datos.message);

      abiertos.delete(it.dataset.pid ?? '');

      this.dispatchEvent(
        new CartAddEvent({}, 'fp-cart-upsell', {
          source: 'fp-cart-upsell',
          itemCount: 1,
          productId: it.dataset.pid,
          variantId: String(id),
          sections: datos.sections,
        })
      );
    } catch {
      this.#encender(it, false);
      abiertos.delete(it.dataset.pid ?? '');

      boton.toggleAttribute('disabled', false);
      if (esBotonAgregar) {
        boton.textContent = 'Sin stock';
        clearTimeout(this.#esperaAviso);
        this.#esperaAviso = setTimeout(() => {
          boton.textContent = etiqueta;
        }, 2000);
      }
    }
  }
}

if (!customElements.get('fp-cart-upsell')) {
  customElements.define('fp-cart-upsell', CartUpsell);
}
